// Modelled directly on sstk-sunder-web's CrosshairEditor.tsx: live-bound
// controls writing straight into the object driving the preview (no separate
// apply step - every change here goes through gunsmithStore, which the
// viewport reads from directly), a preset rail where each preset is its own
// live mini-preview, and a versioned, defensively-clamped share-code
// export/import.

import { useState } from 'react';
import { useGunsmithStore } from '../store/gunsmithStore';
import type { WeaponPartFinish, WeaponPartRole } from '../../../engine/content/schema/weapon';
import { CHASSIS_REGISTRY, COMPONENT_REGISTRY } from '@sstk/weapon-catalogue';
import { SKIN_PRESETS } from './skinPresets';
import { decodeSkinShareCode, encodeSkinShareCode, matchingSkinPreset, WEAPON_PART_ROLES } from './skinShareCode';
import { ColorField, Slider } from './controls';

const ROLE_LABELS: Record<WeaponPartRole, string> = {
  body: 'Body', frame: 'Frame', metal: 'Metal', grip: 'Grip', furniture: 'Furniture',
  detail: 'Detail', sight: 'Sight', optic: 'Optic', energy: 'Energy',
};

const GLOW_ROLES = new Set<WeaponPartRole>(['sight', 'optic', 'energy']);

/** Roles a small swatch preview actually shows: whichever ones a skin overrides, in taxonomy order. */
function presetSwatchRoles(parts: Partial<Record<WeaponPartRole, WeaponPartFinish>>): WeaponPartRole[] {
  return WEAPON_PART_ROLES.filter((role) => parts[role]);
}

function swatchColor(finish: WeaponPartFinish): string {
  const value = finish.emissive ?? finish.color ?? 0x333333;
  return `#${value.toString(16).padStart(6, '0')}`;
}

/** Roles the active weapon's chassis + mounted components actually use, in taxonomy order - what the panel shows controls for. */
function activePartRoles(chassisId: string, componentIds: string[]): WeaponPartRole[] {
  const roles = new Set<WeaponPartRole>();
  const chassis = CHASSIS_REGISTRY[chassisId];
  if (chassis) for (const role of Object.values(chassis.materialRoles)) roles.add(role);
  for (const componentId of componentIds) {
    const component = COMPONENT_REGISTRY[componentId];
    if (component) for (const role of Object.values(component.materialRoles)) roles.add(role);
  }
  return WEAPON_PART_ROLES.filter((role) => roles.has(role));
}

export function MaterialPanel() {
  const weapons = useGunsmithStore((s) => s.weapons);
  const rosterLoaded = useGunsmithStore((s) => s.rosterLoaded);
  const activeWeaponId = useGunsmithStore((s) => s.activeWeaponId);
  const setSkinPartFinish = useGunsmithStore((s) => s.setSkinPartFinish);
  const renameActiveSkin = useGunsmithStore((s) => s.renameActiveSkin);
  const applySkin = useGunsmithStore((s) => s.applySkin);

  const [importCode, setImportCode] = useState('');
  const [importErr, setImportErr] = useState('');
  const [copied, setCopied] = useState(false);

  const active = weapons.find((w) => w.id === activeWeaponId);
  if (!active) {
    // Same roster fetch RosterPanel/AssemblyPanel read rosterLoaded for -
    // without this, this message claimed there was nothing to select at the
    // exact moment the toolbar above was still saying "Loading your weapons...".
    return (
      <p style={{ padding: 14, fontSize: 12, color: 'var(--ss-text-3, #6d747e)' }}>
        {rosterLoaded ? 'Select or create a weapon first.' : 'Loading weapons…'}
      </p>
    );
  }

  const roles = activePartRoles(active.chassisId, active.components.map((m) => m.componentId));
  const activePreset = matchingSkinPreset(active.skin);
  const code = encodeSkinShareCode(active.skin);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable - the field is selectable as a fallback */
    }
  };

  const applyImport = () => {
    const decoded = decodeSkinShareCode(importCode, active.skin.id);
    if (!decoded) {
      setImportErr('Unrecognised code - check it and try again.');
      return;
    }
    setImportErr('');
    setImportCode('');
    applySkin(decoded);
  };

  const set = (role: WeaponPartRole, patch: Partial<WeaponPartFinish>) => {
    const current = active.skin.parts[role] ?? {};
    setSkinPartFinish(role, { ...current, ...patch });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '12px 14px', overflowY: 'auto', flex: 1 }}>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.13em', color: 'var(--ss-text-2, #8b929c)' }}>Skin name</span>
        <input
          className="ss-input"
          value={active.skin.name}
          onChange={(e) => renameActiveSkin(e.target.value)}
          style={{ padding: '7px 10px', fontSize: 12.5, borderRadius: 8 }}
        />
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.13em', color: 'var(--ss-text-2, #8b929c)' }}>Presets</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {SKIN_PRESETS.map((preset) => {
            const isActive = activePreset === preset.id;
            const swatchRoles = presetSwatchRoles(preset.parts);
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applySkin(preset)}
                title={preset.name}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                  padding: '7px 8px', borderRadius: 9, width: 68,
                  border: '1px solid ' + (isActive ? 'var(--ss-accent-border, #6444d6)' : 'var(--ss-border-soft, rgba(255,255,255,0.08))'),
                  background: isActive ? 'var(--ss-accent-soft, rgba(100,68,214,0.16))' : 'var(--ss-hover, rgba(255,255,255,0.05))',
                }}
              >
                <span style={{ display: 'flex', gap: 2 }}>
                  {swatchRoles.length === 0 ? (
                    <span style={{ width: 10, height: 10, borderRadius: 999, border: '1px solid var(--ss-border, rgba(255,255,255,0.16))' }} />
                  ) : (
                    swatchRoles.slice(0, 4).map((role) => (
                      <span
                        key={role}
                        style={{
                          width: 10, height: 10, borderRadius: 999,
                          background: swatchColor(preset.parts[role]!),
                          boxShadow: GLOW_ROLES.has(role) ? `0 0 4px ${swatchColor(preset.parts[role]!)}` : undefined,
                        }}
                      />
                    ))
                  )}
                </span>
                <span style={{ fontSize: 9.5, fontWeight: 500, color: isActive ? 'var(--ss-accent, #6444d6)' : 'var(--ss-text-2, #8b929c)', textAlign: 'center', lineHeight: 1.2 }}>
                  {preset.name}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.13em', color: 'var(--ss-text-2, #8b929c)' }}>Parts</span>
        {roles.length === 0 && (
          <p style={{ fontSize: 11.5, color: 'var(--ss-text-3, #6d747e)', margin: 0 }}>
            This chassis/component combination has no catalogued material roles yet.
          </p>
        )}
        {roles.map((role) => {
          const finish = active.skin.parts[role];
          const glow = GLOW_ROLES.has(role);
          return (
            <div
              key={role}
              style={{
                border: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))',
                borderRadius: 8, padding: '9px 10px', display: 'flex', flexDirection: 'column', gap: 8,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{ROLE_LABELS[role]}</span>
                {finish && (
                  <button type="button" className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => setSkinPartFinish(role, undefined)}>
                    Reset
                  </button>
                )}
              </div>
              <ColorField label="Colour" value={finish?.color ?? 0x808080} onChange={(hex) => set(role, { color: hex })} />
              <Slider label="Roughness" value={finish?.roughness ?? 0.5} min={0} max={1} step={0.01} format={(v) => v.toFixed(2)} onChange={(v) => set(role, { roughness: v })} />
              <Slider label="Metalness" value={finish?.metalness ?? 0} min={0} max={1} step={0.01} format={(v) => v.toFixed(2)} onChange={(v) => set(role, { metalness: v })} />
              {glow && (
                <>
                  <ColorField label="Emissive" value={finish?.emissive ?? 0x000000} onChange={(hex) => set(role, { emissive: hex })} />
                  <Slider label="Glow" value={finish?.emissiveIntensity ?? 0} min={0} max={4} step={0.05} format={(v) => v.toFixed(2)} onChange={(v) => set(role, { emissiveIntensity: v })} />
                </>
              )}
            </div>
          );
        })}
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 6, borderTop: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ width: 70, flexShrink: 0, fontSize: 10.5, color: 'var(--ss-text-2, #8b929c)' }}>Share code</span>
          <input
            className="ss-input"
            readOnly
            value={code}
            onFocus={(e) => e.currentTarget.select()}
            style={{ flex: 1, padding: '6px 9px', fontSize: 11, fontFamily: 'var(--ss-mono, monospace)', borderRadius: 7 }}
            aria-label="Your skin share code"
          />
          <button type="button" className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => void copy()}>
            {copied ? 'Copied ✓' : 'Copy'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ width: 70, flexShrink: 0, fontSize: 10.5, color: 'var(--ss-text-2, #8b929c)' }}>Import</span>
          <input
            className="ss-input"
            placeholder="Paste a code..."
            value={importCode}
            spellCheck={false}
            onChange={(e) => { setImportCode(e.target.value); setImportErr(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') applyImport(); }}
            style={{ flex: 1, padding: '6px 9px', fontSize: 11, fontFamily: 'var(--ss-mono, monospace)', borderRadius: 7 }}
            aria-label="Paste a skin share code"
          />
          <button type="button" className="ss-btn ss-btn--primary ss-btn--sm" onClick={applyImport} disabled={!importCode.trim()}>
            Apply
          </button>
        </div>
        {importErr && <p style={{ fontSize: 11, color: 'var(--ss-danger, #d0453f)', margin: 0 }}>{importErr}</p>}
      </section>
    </div>
  );
}
